import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { endOfYear, startOfYear } from "date-fns";
import type { DateRange } from "react-day-picker";
import { cn } from "@/shared/lib/utils";
import { useTranslation } from "@/shared/i18n";

interface YearGridPickerProps {
  selected: DateRange | undefined;
  onSelect: (range: DateRange | undefined) => void;
  pageSize?: number;
}

function buildYear(year: number): Date {
  return new Date(year, 0, 1);
}

function getYearState(
  cellYear: number,
  range: DateRange | undefined
): "start" | "end" | "middle" | "single" | null {
  if (!range?.from) return null;
  const fromYear = range.from.getFullYear();
  const toYear = range.to ? range.to.getFullYear() : null;

  if (toYear === null) return cellYear === fromYear ? "single" : null;
  if (cellYear === fromYear && cellYear === toYear) return "single";
  if (cellYear === fromYear) return "start";
  if (cellYear === toYear) return "end";
  if (cellYear > fromYear && cellYear < toYear) return "middle";
  return null;
}

export function YearGridPicker({
  selected,
  onSelect,
  pageSize = 12,
}: YearGridPickerProps) {
  const { t } = useTranslation("date-range-picker");
  const [pageStart, setPageStart] = useState<number>(() => {
    const ref = selected?.from?.getFullYear() ?? new Date().getFullYear();
    return Math.floor(ref / pageSize) * pageSize;
  });

  const years = Array.from({ length: pageSize }, (_, i) => pageStart + i);
  const pageEnd = pageStart + pageSize - 1;

  const handleClick = (cellYear: number) => {
    const cellStart = startOfYear(buildYear(cellYear));
    const cellEnd = endOfYear(buildYear(cellYear));

    if (!selected?.from || (selected.from && selected.to)) {
      onSelect({ from: cellStart, to: cellEnd });
      return;
    }
    const fromYear = selected.from.getFullYear();
    if (cellYear < fromYear) {
      onSelect({ from: cellStart, to: endOfYear(buildYear(fromYear)) });
    } else {
      onSelect({ from: startOfYear(buildYear(fromYear)), to: cellEnd });
    }
  };

  return (
    <div className="select-none w-full max-w-[320px]">
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => setPageStart((y) => y - pageSize)}
          className="relative h-9 w-9 grid place-items-center rounded-md text-white/70 hover:bg-white/[0.08] hover:text-white before:absolute before:inset-[-4px] before:content-['']"
          aria-label={t("yearGrid.previousPage")}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-medium text-white/90" aria-live="polite">
          {pageStart} – {pageEnd}
        </span>
        <button
          type="button"
          onClick={() => setPageStart((y) => y + pageSize)}
          className="relative h-9 w-9 grid place-items-center rounded-md text-white/70 hover:bg-white/[0.08] hover:text-white before:absolute before:inset-[-4px] before:content-['']"
          aria-label={t("yearGrid.nextPage")}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-1">
        {years.map((y) => {
          const state = getYearState(y, selected);
          return (
            <button
              key={y}
              type="button"
              onClick={() => handleClick(y)}
              aria-pressed={state !== null}
              className={cn(
                "h-11 rounded-md text-sm font-medium transition-colors text-white/80",
                "hover:bg-white/[0.06] hover:text-white",
                "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/60",
                state === "start" &&
                  "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground rounded-r-none",
                state === "end" &&
                  "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground rounded-l-none",
                state === "middle" &&
                  "bg-white/[0.06] text-white hover:bg-white/[0.08] rounded-none",
                state === "single" &&
                  "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
              )}
            >
              {y}
            </button>
          );
        })}
      </div>
    </div>
  );
}
